
(function () {
  'use strict';

  var VIEW_KEY = 'collection-view';

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\\]]/g, '\\$&');
  }

  var entityDecoder = null;
  function decodeEntities(value) {
    if (value == null || value.indexOf('&') === -1) return value;
    if (!entityDecoder) entityDecoder = document.createElement('textarea');
    entityDecoder.innerHTML = value;
    return entityDecoder.value;
  }

  /* ── Section root (sort, grid switcher, facets UX) ─────────────── */
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId; 
      this.resultsUrl = this.dataset.resultsUrl || this.dataset.collectionUrl || window.location.pathname;
      this.collectionUrl = this.resultsUrl; 
      this.baseParams = new URLSearchParams(this.dataset.baseParams || '');
      this.controller = null;

      this.applyStoredView();
      this.initPriceRanges();
      this.initLoadMore();
      this.initGridSwitcher();

      this.addEventListener('click', (event) => {
        var loadMoreBtn = event.target.closest('[data-load-more-btn]');
        if (loadMoreBtn) {
          event.preventDefault();
          this.loadNextPage();
          return;
        }
        var sortBtn = event.target.closest('[data-collection-sort]');
        if (sortBtn) {
          event.preventDefault();
          this.onSortChange(sortBtn.getAttribute('data-sort-value'));
          return;
        }
        var viewBtn = event.target.closest('[data-grid-view]');
        if (viewBtn) {
          event.preventDefault();
          this.setView(viewBtn.getAttribute('data-grid-view'));
          return;
        }
        var gridBtn = event.target.closest('.enable_grid_switcher button');
        if (gridBtn && this.contains(gridBtn)) {
          var group = gridBtn.closest('.enable_grid_switcher');
          var grid = this.querySelector('[data-collection-grid]');
          var self = this;
          this.fadeGrid(grid, function () {
            group.querySelectorAll('button').forEach(function (b) { b.classList.remove('active'); });
            gridBtn.classList.add('active');
            self.syncGridSwitcherPressed();
          });
          this.updateGridSwitcherVisibility(group, gridBtn);
          return;
        }
        var sidebarToggle = event.target.closest('[data-sidebar-toggle]');
        if (sidebarToggle && this.contains(sidebarToggle)) {
          var body = this.querySelector('.collection-tpl__body');
          if (body) {
            var collapsed = body.classList.toggle('is-filters-collapsed');
            sidebarToggle.classList.toggle('collapsed', collapsed);
            sidebarToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
          }
          return;
        }
        var toggle = event.target.closest('[data-facet-toggle]');
        if (toggle) {
          event.preventDefault();
          this.toggleFacetList(toggle);
          return;
        }
        var link = event.target.closest(
          '[data-collection-clear], .collection-active-filters__chip, .collection-active-filters__clear, [data-collection-pagination] a'
        );
        if (link && link.getAttribute('href')) {
          event.preventDefault();
          this.navigate(link.getAttribute('href'), { scroll: link.closest('[data-collection-pagination]') != null });
        }
      });

      this.addEventListener('change', (event) => {
        var checkbox = event.target.closest('[data-facet-checkbox]');
        if (checkbox) {
          var form = checkbox.closest('[data-filter-form]');
          if (form) this.navigate(this.buildUrl(form));
        }
      });
      this.addEventListener('submit', (event) => {
        var form = event.target.closest('[data-filter-form]');
        if (!form) return;
        event.preventDefault();
        if (!this.validatePrice(form)) return;
        this.closeDrawer();
        this.navigate(this.buildUrl(form));
      });
      if (!this._onPop) {
        this._onPop = () => this.fetchAndReplace(window.location.href, { push: false });
        window.addEventListener('popstate', this._onPop);
      }
    }

    disconnectedCallback() {
      if (this.controller) { this.controller.abort(); this.controller = null; }
      if (this._onPop) { window.removeEventListener('popstate', this._onPop); this._onPop = null; }
      this.abortLoadMore();
      if (this.io) { this.io.disconnect(); this.io = null; }
      if (this.gridSwitcherQueries) {
        this.gridSwitcherQueries.forEach(function (mq) {
          mq.removeEventListener('change', this.syncGridSwitcherPressed);
        }, this);
        this.gridSwitcherQueries = null;
      }
    }

    /* Grid switcher */
    setView(view) {
      var isList = view === 'list';
      this.classList.toggle('collection-tpl--view-list', isList);
      this.querySelectorAll('[data-grid-view]').forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-grid-view') === view ? 'true' : 'false');
      });
      try { localStorage.setItem(VIEW_KEY, view); } catch (e) { /* ignore */ }
    }

    applyStoredView() {
      var view = 'grid';
      try { view = localStorage.getItem(VIEW_KEY) || 'grid'; } catch (e) { /* ignore */ }
      if (this.querySelector('[data-grid-view]')) this.setView(view);
    }

    /* Facet "show more / less" */
    setFacetGroupExpanded(group, expanded) {
      var list = group.querySelector('[data-facet-list]');
      var toggle = group.querySelector('[data-facet-toggle]');
      if (list) list.classList.toggle('is-expanded', expanded);
      if (toggle) {
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        var more = toggle.querySelector('[data-facet-toggle-more]');
        var less = toggle.querySelector('[data-facet-toggle-less]');
        if (more) more.hidden = expanded;
        if (less) less.hidden = !expanded;
      }
    }
    toggleFacetList(toggle) {
      var group = toggle.closest('[data-facet-group]');
      if (!group) return;
      var expand = toggle.getAttribute('aria-expanded') !== 'true';
      this.setFacetGroupExpanded(group, expand);
      if (expand) { 
        var first = group.querySelector('.collection-facets__item--extra input:not(:disabled)');
        if (first) first.focus();
      }
    }
    onSortChange(value) {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');
      this.navigate(url.pathname + url.search);
    }

    /* ── Grid switcher (column count buttons) ──────────────────── */
    initGridSwitcher() {
      var group = this.querySelector('.enable_grid_switcher');
      if (!group) return;
      var active = group.querySelector('button.active');
      if (active) this.updateGridSwitcherVisibility(group, active);
 
      this.gridSwitcherQueries = ['(max-width: 575.98px)', '(max-width: 991.98px)', '(max-width: 1399.98px)'].map(
        function (q) {
          return window.matchMedia(q);
        }
      );
      this.syncGridSwitcherPressed = this.syncGridSwitcherPressed.bind(this);
      this.gridSwitcherQueries.forEach(function (mq) {
        mq.addEventListener('change', this.syncGridSwitcherPressed);
      }, this);
      this.syncGridSwitcherPressed();
    }

    /* Effective column count at the current viewport, matching the CSS ladder.  */
    effectiveColumns(chosen) {
      var w = window.innerWidth;
      if (w < 576) return null;
      if (w < 992) return chosen >= 5 ? 3 : Math.min(chosen, 2);
      if (w < 1400) return chosen >= 5 ? 4 : Math.min(chosen, 3);
      return chosen;
    }

    syncGridSwitcherPressed() {
      var group = this.querySelector('.enable_grid_switcher');
      if (!group) return;
      var classes = { two_column: 2, three_column: 3, four_column: 4, five_column: 5 };
      var active = group.querySelector('button.active');
      var chosen = 0;
      if (active) {
        Object.keys(classes).forEach(function (c) {
          if (active.classList.contains(c)) chosen = classes[c];
        });
      }
      var effective = chosen ? this.effectiveColumns(chosen) : null;
      group.querySelectorAll('button').forEach(function (btn) {
        var n = 0;
        Object.keys(classes).forEach(function (c) {
          if (btn.classList.contains(c)) n = classes[c];
        });
        btn.setAttribute('aria-pressed', effective !== null && n === effective ? 'true' : 'false');
      });
    }

    updateGridSwitcherVisibility(group, activeButton) {
      var isOneColumnActive = activeButton.classList.contains('one_column');
      var twoColumnBtn = group.querySelector('.two_column');
      var fourColumnBtn = group.querySelector('.four_column');
      if (twoColumnBtn) {
        twoColumnBtn.style.cssText = isOneColumnActive ? 'display: none !important' : '';
      }
      if (fourColumnBtn) {
        fourColumnBtn.style.cssText = isOneColumnActive ? 'display: none !important' : '';
      }
    }

    /* Crossfade the grid while the column count changes */
    fadeGrid(grid, mutate) {
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!grid || reduce) { mutate(); return; }

      grid.style.transition = 'opacity 0.1s ease';
      grid.style.opacity = '0';
      window.setTimeout(function () {
        mutate();
        requestAnimationFrame(function () {
          grid.style.opacity = '1';
          window.setTimeout(function () {
            grid.style.transition = '';
            grid.style.opacity = '';
          }, 250);
        });
      }, 200);
    }

    /* ── AJAX core ─────────────────────────────────────────────── */
    buildUrl(form) {
      var params = new URLSearchParams(); 
      var baseKeys = [];
      this.baseParams.forEach(function (value, key) {
        params.append(key, value);
        if (baseKeys.indexOf(key) === -1) baseKeys.push(key);
      });
      /* A bound sitting on the edge of the range filters nothing — drop it so
         the URL stays clean and the active-filter chip reflects reality. */
      var skip = [];
      form.querySelectorAll('[data-price-range]').forEach(function (range) {
        var lowest = parseFloat(range.dataset.min) || 0;
        var highest = parseFloat(range.dataset.max) || 0;
        var boxMin = range.querySelector('[data-facet-price-min]');
        var boxMax = range.querySelector('[data-facet-price-max]');
        if (boxMin && boxMin.name && parseFloat(boxMin.value) === lowest) skip.push(boxMin.name);
        if (boxMax && boxMax.name && parseFloat(boxMax.value) === highest) skip.push(boxMax.name);
      });

      var data = new FormData(form);
      data.forEach(function (value, key) {
        if (value === '' || value == null) return;
        if (baseKeys.indexOf(key) !== -1) return;
        if (skip.indexOf(key) !== -1) return;
        params.append(key, value);
      });
      var sort = this.querySelector('[data-collection-sort].is-active');
      var sortValue = sort && sort.getAttribute('data-sort-value');
      if (sortValue) {
        params.delete('sort_by');
        params.set('sort_by', sortValue);
      }
      params.delete('page');
      var qs = params.toString();
      return this.resultsUrl + (qs ? '?' + qs : '');
    }

    navigate(url, opts) {
      opts = opts || {};
      this.fetchAndReplace(url, { push: true, scroll: opts.scroll });
    }

    fetchAndReplace(url, opts) {
      opts = opts || {};
      var self = this;
      if (this.controller) this.controller.abort(); 
      this.abortLoadMore();
      this.controller = 'AbortController' in window ? new AbortController() : null;

      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');

      var sep = url.indexOf('?') === -1 ? '?' : '&';
      var fetchUrl = url + sep + 'section_id=' + this.sectionId;

      fetch(fetchUrl, this.controller ? { signal: this.controller.signal } : {})
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var next = doc.getElementById('shopify-section-' + self.sectionId) || doc.querySelector('[data-section-id="' + self.sectionId + '"]');
          if (!next) throw new Error('Section markup not found');

          try {
            var focusKey = self.captureFocus();
            self.applySwap(next);
            self.restoreFocus(focusKey);
            if (!focusKey && (opts.scroll || /[?&]page=/.test(url))) self.focusResults();
            if (opts.push !== false) {
              var clean = url.replace(/([?&])section_id=[^&]*&?/, '$1').replace(/[?&]$/, '');
              window.history.pushState({ collectionFilters: true }, '', clean);
            }
            if (opts.scroll) self.scrollToResults();
          } catch (swapErr) {
            if (window.console) console.error('[collection-filters] swap failed', swapErr);
          }
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (window.console) console.error('[collection-filters] fetch failed', err);
        })
        .then(function () {
          self.classList.remove('is-loading');
          self.removeAttribute('aria-busy');
          self.controller = null;
        });
    }

    /* Swap the freshly-rendered regions into the live DOM. */
    applySwap(next) {
      var self = this;
      this.swap('[data-collection-grid]', next);
      this.swapOrRemove('[data-collection-pagination]', next);
      this.swapLoadMore(next);

      this.swap('[data-collection-count]', next);
      this.swap('[data-collection-page-status]', next);
      this.swap('[data-filter-trigger-count]', next);
      this.swap('[data-collection-sort-wrap]', next);

      this.swapOrClear('[data-active-filters-wrap]', next);

      var drawerOpen = this.isDrawerOpen();
      this.querySelectorAll('[data-filter-form]').forEach(function (form) {
        var inDrawer = form.closest('.collection-tpl__drawer-form') != null;
        if (inDrawer && drawerOpen) return;
        var facets = form.querySelector('[data-facets]');
        var incomingForm = self.matchForm(next, inDrawer);
        var incomingFacets = incomingForm && incomingForm.querySelector('[data-facets]');
        if (facets && incomingFacets) { 
          var expandedKeys = [];
          facets.querySelectorAll('[data-facet-group]').forEach(function (g) {
            var list = g.querySelector('[data-facet-list]');
            if (list && list.classList.contains('is-expanded')) {
              expandedKeys.push(g.getAttribute('data-facet-group') || '');
            }
          });
          if (window.bootstrap && bootstrap.Collapse) {
            facets.querySelectorAll('.collapse').forEach(function (el) {
              var inst = bootstrap.Collapse.getInstance(el);
              if (inst) inst.dispose();
            });
          }
          facets.innerHTML = incomingFacets.innerHTML;
          expandedKeys.forEach(function (paramName) {
            if (!paramName) return;
            var g = facets.querySelector('[data-facet-group="' + cssEscape(paramName) + '"]');
            if (g) self.setFacetGroupExpanded(g, true);
          });
        }
      });

      if (drawerOpen) this.syncDrawerCounts(next);

      this.initPriceRanges();
      this.initLoadMore();

      if (window.SectionCarousel && typeof window.SectionCarousel.initIn === 'function') {
        try { window.SectionCarousel.initIn(this); } catch (e) {   }
      }
    }

    
    swapLoadMore(next) {
      var live = this.querySelector('[data-collection-load-more]');
      var incoming = next.querySelector('[data-collection-load-more]');
      if (!incoming) { if (live) live.remove(); return; }
      if (!live) {
         var host = this.querySelector('[data-collection-results]');
        if (!host) return;
        host.appendChild(incoming.cloneNode(true));
        return;
      }
      live.innerHTML = incoming.innerHTML;
      live.setAttribute('data-next-url', incoming.getAttribute('data-next-url') || '');
      live.setAttribute('data-mode', incoming.getAttribute('data-mode') || '');
      live.classList.remove('is-loading-more');
    }

    swap(selector, next) {
      var live = this.querySelector(selector);
      var incoming = next.querySelector(selector);
      if (live && incoming) live.innerHTML = incoming.innerHTML;
    }
    swapOrRemove(selector, next) {
      var live = this.querySelector(selector);
      var incoming = next.querySelector(selector);
      if (live && incoming) { live.innerHTML = incoming.innerHTML; live.hidden = false; }
      else if (live && !incoming) { live.innerHTML = ''; live.hidden = true; }
    }
    swapOrClear(selector, next) {
      var live = this.querySelector(selector);
      var incoming = next.querySelector(selector);
      if (!live) return;
      live.innerHTML = incoming ? incoming.innerHTML : '';
    }
    matchForm(next, inDrawer) {
      var forms = next.querySelectorAll('[data-filter-form]');
      for (var i = 0; i < forms.length; i++) {
        var isDrawer = forms[i].closest('.collection-tpl__drawer-form') != null;
        if (isDrawer === inDrawer) return forms[i];
      }
      return null;
    }

    
    syncDrawerCounts(next) {
      var liveForm = this.querySelector('.collection-tpl__drawer-form[data-filter-form]');
      var incomingForm = this.matchForm(next, true);
      var facets = liveForm && liveForm.querySelector('[data-facets]');
      var incomingFacets = incomingForm && incomingForm.querySelector('[data-facets]');
      if (!facets || !incomingFacets) return;

      facets.querySelectorAll('[data-facet-group]').forEach(function (group) {
        var key = group.getAttribute('data-facet-group') || '';
        if (!key) return;
        var incomingGroup = incomingFacets.querySelector('[data-facet-group="' + cssEscape(key) + '"]');
        if (!incomingGroup) return;

        
        var legend = group.querySelector('.collection-facets__legend');
        var liveCount = group.querySelector('.collection-facets__legend-count');
        var incomingCount = incomingGroup.querySelector('.collection-facets__legend-count');
        if (liveCount && incomingCount) {
          liveCount.textContent = incomingCount.textContent;
        } else if (!liveCount && incomingCount && legend) {
          var icon = legend.querySelector('.collection-facets__legend-icon');
          legend.insertBefore(incomingCount.cloneNode(true), icon);
        } else if (liveCount && !incomingCount) {
          liveCount.remove();
        }

       
        group.querySelectorAll('[data-facet-checkbox]').forEach(function (input) {
          var incomingInput = incomingGroup.querySelector(
            '[data-facet-checkbox][name="' + cssEscape(input.name) + '"][value="' + cssEscape(input.value) + '"]'
          );
          if (!incomingInput) return;
          input.disabled = incomingInput.disabled; 
          input.checked = incomingInput.hasAttribute('checked');
          var label = input.closest('label');
          var incomingLabel = incomingInput.closest('label');
          if (!label || !incomingLabel) return;
          label.classList.toggle('is-selected', incomingLabel.classList.contains('is-selected'));
          var optCount = label.querySelector('.collection-facets__option-count');
          var incomingOptCount = incomingLabel.querySelector('.collection-facets__option-count');
          if (optCount && incomingOptCount) optCount.textContent = incomingOptCount.textContent;
        });
      });
 
      facets.querySelectorAll('[data-price-range]').forEach(function (range, index) {
        var incomingRange = incomingFacets.querySelectorAll('[data-price-range]')[index];
        if (!incomingRange) return; 
        [
          '[data-facet-price-min]',
          '[data-facet-price-max]',
          '[data-range-thumb-min]',
          '[data-range-thumb-max]'
        ].forEach(function (selector) {
          var live = range.querySelector(selector);
          var incoming = incomingRange.querySelector(selector);
          if (live && incoming) live.value = incoming.getAttribute('value') || '';
        });

        var thumb = range.querySelector('[data-range-thumb-min]');
        if (thumb) thumb.dispatchEvent(new Event('input', { bubbles: false }));
      });

      if (liveForm) this.validatePrice(liveForm);
    }

    isDrawerOpen() {
      return !!this.querySelector('.collection-tpl__drawer.show');
    }
    closeDrawer() {
      var el = this.querySelector('.collection-tpl__drawer');
      if (el && window.bootstrap && bootstrap.Offcanvas) {
        bootstrap.Offcanvas.getOrCreateInstance(el).hide();
      }
    }
    scrollToResults() {
      var results = this.querySelector('[data-collection-results]');
      if (!results) return;
      var top = results.getBoundingClientRect().top + window.pageYOffset - 20;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
 
    focusResults() {
      var target = this.querySelector(
        '[data-collection-grid] a[href], ' +
        '[data-collection-grid] button:not([disabled]), ' +
        '[data-collection-grid] [tabindex]:not([tabindex="-1"])'
      );
      if (!target) { 
        target = this.querySelector('[data-collection-results]');
        if (!target) { this.resetFocusToTop(); return; }
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      }
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
    }

    resetFocusToTop() {
      var body = document.body;
      if (!body) return;
      var hadTabindex = body.hasAttribute('tabindex');
      if (!hadTabindex) body.setAttribute('tabindex', '-1');
      try {
        body.focus({ preventScroll: true });
      } catch (e) {
        body.focus();
      }
      if (!hadTabindex) body.removeAttribute('tabindex');
    }

    captureFocus() {
      var el = document.activeElement;
      if (!el || !this.contains(el)) return null;
      if (el.name && (el.type === 'checkbox' || el.type === 'radio')) { 
        return {
          sel: 'input[name="' + cssEscape(el.name) + '"][value="' + cssEscape(el.value) + '"]',
          drawer: el.closest('.collection-tpl__drawer-form') != null
        };
      }
      if (el.matches && el.matches('[data-facet-price-min], [data-facet-price-max]')) { 
        var attr = el.hasAttribute('data-facet-price-min') ? 'data-facet-price-min' : 'data-facet-price-max';
        var sel = el.id ? '#' + cssEscape(el.id) : '[' + attr + ']';
        return { sel: sel, caret: true };
      }
      if (el.matches && el.matches('[data-range-thumb-min], [data-range-thumb-max]')) {
        var thumbAttr = el.hasAttribute('data-range-thumb-min') ? 'data-range-thumb-min' : 'data-range-thumb-max';
        return {
          sel: el.id ? '#' + cssEscape(el.id) : '[' + thumbAttr + ']',
          drawer: el.closest('.collection-tpl__drawer-form') != null
        };
      }
      return null;
    }
    restoreFocus(key) {
      if (!key) return;
      var target = null;
      if (typeof key.drawer === 'boolean') {
        var candidates = this.querySelectorAll(key.sel);
        for (var i = 0; i < candidates.length; i++) {
          if ((candidates[i].closest('.collection-tpl__drawer-form') != null) === key.drawer) {
            target = candidates[i];
            break;
          }
        }
      }
      if (!target) target = this.querySelector(key.sel);
      if (!target) return; 
      if (target.closest('.collection-facets__item--extra')) {
        var list = target.closest('[data-facet-list]');
        if (list && list.classList.contains('is-collapsible') && !list.classList.contains('is-expanded')) {
          var group = target.closest('[data-facet-group]');
          if (group) this.setFacetGroupExpanded(group, true);
        }
      }
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
      if (key.caret && typeof target.value === 'string') {
        var len = target.value.length;
        try { target.setSelectionRange(len, len); } catch (e) {   }
      }
    }

    /* Dual-handle price range sliders */
    initPriceRanges() {
      var self = this;
      this.querySelectorAll('[data-price-range]').forEach(function (range) {
        if (range.dataset.rangeReady === 'true') return;
        range.dataset.rangeReady = 'true';
        self.setupPriceRange(range);
      });
    }

    setupPriceRange(range) {
      var self = this;
      var thumbMin = range.querySelector('[data-range-thumb-min]');
      var thumbMax = range.querySelector('[data-range-thumb-max]');
      var boxMin = range.querySelector('[data-facet-price-min]');
      var boxMax = range.querySelector('[data-facet-price-max]');
      if (!thumbMin || !thumbMax || !boxMin || !boxMax) return;

      var floor = parseFloat(range.dataset.min) || 0;
      var ceil = parseFloat(range.dataset.max) || 0;
      var form = range.closest('[data-filter-form]');

      /* Max must stay strictly greater than min, so the two thumbs are kept at
         least one step apart rather than being allowed to collapse together. */
      var step = parseFloat(thumbMin.step) || 1;
      var gap = step;
      var hasRoom = ceil - floor >= gap;

      function clampPair(source) {
        var lo = parseFloat(thumbMin.value);
        var hi = parseFloat(thumbMax.value);
        if (isNaN(lo)) lo = floor;
        if (isNaN(hi)) hi = ceil;

        if (!hasRoom) {
          lo = floor;
          hi = ceil;
        } else if (source === 'min') {
          lo = Math.max(floor, Math.min(lo, hi - gap));
        } else {
          hi = Math.min(ceil, Math.max(hi, lo + gap));
        }

        thumbMin.value = lo;
        thumbMax.value = hi;
        return { lo: lo, hi: hi };
      }

      function paint(lo, hi) {
        var span = ceil - floor || 1;
        var minPct = ((lo - floor) / span) * 100;
        var maxPct = ((hi - floor) / span) * 100;
        range.style.setProperty('--range-min-pct', minPct + '%');
        range.style.setProperty('--range-max-pct', maxPct + '%'); 
        var overlapped = maxPct - minPct < 5;
        range.classList.toggle('is-thumbs-overlap-high', overlapped && minPct > 50);
      }

      paint(parseFloat(thumbMin.value), parseFloat(thumbMax.value));

      function onThumbInput(source) {
        var v = clampPair(source);
        boxMin.value = v.lo;
        boxMax.value = v.hi;
        paint(v.lo, v.hi); 
        if (form) self.validatePrice(form);
      }
      thumbMin.addEventListener('input', function () { onThumbInput('min'); });
      thumbMax.addEventListener('input', function () { onThumbInput('max'); });

      /* Mirror the boxes onto the thumbs while typing. Deliberately does not
         rewrite the box being edited — the value is only validated, so a
         half-typed number is never mangled mid-keystroke. */
      function onBoxInput() {
        var lo = boxMin.value === '' ? floor : parseFloat(boxMin.value);
        var hi = boxMax.value === '' ? ceil : parseFloat(boxMax.value);
        if (isNaN(lo)) lo = floor;
        if (isNaN(hi)) hi = ceil;
        lo = Math.max(floor, Math.min(lo, ceil));
        hi = Math.max(floor, Math.min(hi, ceil));
        thumbMin.value = lo;
        thumbMax.value = hi;
        paint(Math.min(lo, hi), Math.max(lo, hi));
        if (form) self.validatePrice(form);
      }
      boxMin.addEventListener('input', onBoxInput);
      boxMax.addEventListener('input', onBoxInput);

      /* Hard clamp into range on blur, once the user has finished typing. */
      function normalise(box) {
        if (box.value === '') return;
        var v = parseFloat(box.value);
        if (isNaN(v)) { box.value = ''; return; }
        v = Math.round(v / step) * step;
        var lowest = box === boxMax && hasRoom ? floor + gap : floor;
        var highest = box === boxMin && hasRoom ? ceil - gap : ceil;
        v = Math.max(lowest, Math.min(v, highest));
        box.value = v;
        onBoxInput();
      }
      boxMin.addEventListener('blur', function () { normalise(boxMin); });
      boxMax.addEventListener('blur', function () { normalise(boxMax); });

      function commit() {
        if (!form) return;
        if (!self.validatePrice(form)) return;
        var url = self.buildUrl(form); 
        var current = window.location.pathname + window.location.search;
        if (url === current) return;
        self.navigate(url);
      }
 
      function commitSoon() {
        if (range.commitTimer) clearTimeout(range.commitTimer);
        range.commitTimer = setTimeout(function () {
          range.commitTimer = null;
          commit();
        }, 300);
      }

      thumbMin.addEventListener('change', commitSoon);
      thumbMax.addEventListener('change', commitSoon);
      boxMin.addEventListener('change', commit);
      boxMax.addEventListener('change', commit);
    }

    /* ── Load more / infinite scroll ───────────────────────────── */
    initLoadMore() {
      if (this.io) { this.io.disconnect(); this.io = null; }

      var footer = this.querySelector('[data-collection-load-more]');
      if (!footer) return;
      if (footer.getAttribute('data-mode') !== 'infinite_scroll') return;
      if (!footer.getAttribute('data-next-url')) return;
      if (!('IntersectionObserver' in window)) return;

      var self = this;
      this.io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) { self.loadNextPage(); break; }
          }
        },
        { rootMargin: '400px 0px' }
      );
      this.io.observe(footer);
    }

    abortLoadMore() {
      if (this.moreController) { this.moreController.abort(); this.moreController = null; }
      this.moreToken = (this.moreToken || 0) + 1;
      this.isLoadingMore = false;
    }

    loadNextPage() {
      if (this.isLoadingMore) return;

      var footer = this.querySelector('[data-collection-load-more]');
      if (!footer) return;
      var nextUrl = footer.getAttribute('data-next-url');
      if (!nextUrl) return;

      var self = this;
      var grid = this.querySelector('[data-collection-grid]');
      var btn = footer.querySelector('[data-load-more-btn]');
      if (!grid) return;

      this.isLoadingMore = true;
      this.moreController = 'AbortController' in window ? new AbortController() : null;
      // Token so a superseded request (aborted by a filter navigation) cannot
      // clobber the state belonging to whatever replaced it.
      var token = (this.moreToken = (this.moreToken || 0) + 1);
      footer.classList.add('is-loading-more');
      if (btn) btn.setAttribute('aria-busy', 'true');

      var sep = nextUrl.indexOf('?') === -1 ? '?' : '&';
      var fetchUrl = nextUrl + sep + 'section_id=' + this.sectionId;

      fetch(fetchUrl, this.moreController ? { signal: this.moreController.signal } : {})
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          if (token !== self.moreToken) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var next =
            doc.getElementById('shopify-section-' + self.sectionId) ||
            doc.querySelector('[data-section-id="' + self.sectionId + '"]');
          if (!next) throw new Error('Section markup not found');

          var incomingGrid = next.querySelector('[data-collection-grid]');
          if (!incomingGrid) throw new Error('Grid not found');

         
          var firstNew = incomingGrid.firstElementChild;
          while (incomingGrid.firstChild) grid.appendChild(incomingGrid.firstChild);

          self.updateLoadMoreState(footer, next);
 
          if (window.SectionCarousel && typeof window.SectionCarousel.initIn === 'function') {
            try { window.SectionCarousel.initIn(grid); } catch (e) {   }
          }

          if (footer.getAttribute('data-mode') === 'load_more' && firstNew) {
            var focusable = firstNew.querySelector('a, button');
            if (focusable) focusable.focus();
          }
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (window.console) console.error('[collection-filters] load more failed', err);
        })
        .then(function () {
          footer.classList.remove('is-loading-more');
          if (btn) btn.removeAttribute('aria-busy');
          if (token !== self.moreToken) return;
          self.isLoadingMore = false;
          self.moreController = null;
          self.initLoadMore();
        });
    }
 
    updateLoadMoreState(footer, next) {
      var incoming = next.querySelector('[data-collection-load-more]');
      var nextUrl = incoming ? incoming.getAttribute('data-next-url') || '' : '';
      footer.setAttribute('data-next-url', nextUrl);

      var liveCount = footer.querySelector('[data-load-more-count]');
      var incomingCount = incoming && incoming.querySelector('[data-load-more-count]');
      if (liveCount && incomingCount) liveCount.textContent = incomingCount.textContent;

      var btn = footer.querySelector('[data-load-more-btn]');
      if (btn) btn.hidden = !nextUrl;

      var end = footer.querySelector('[data-load-more-end]');
      if (end) end.hidden = !!nextUrl;
    }

    validatePrice(form) {
      var minInput = form.querySelector('[data-facet-price-min]');
      var maxInput = form.querySelector('[data-facet-price-max]');
      var errorEl = form.querySelector('[data-facet-price-error]');
      if (!minInput || !maxInput) return true;

      var min = minInput.value === '' ? null : parseFloat(minInput.value);
      var max = maxInput.value === '' ? null : parseFloat(maxInput.value);
      if (isNaN(min)) min = null;
      if (isNaN(max)) max = null;
      var message = '';

      /* Strictly greater: an equal pair is rejected, not just an inverted one. */
      if (min !== null && max !== null && min >= max) {
        message = maxInput.getAttribute('data-error-compare') || 'Max price must be greater than min price.';
      }

      var invalid = message !== '';
      [minInput, maxInput].forEach(function (input) {
        if (invalid) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      });

      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = !invalid;
      }
      return !invalid;
    }
  }

  if (!customElements.get('collection-filters')) {
    customElements.define('collection-filters', CollectionFilters);
  }
 
  /* ── Search results (mixed-type client-side pagination) ────────── */
  class SearchFilters extends CollectionFilters {
    connectedCallback() {
      super.connectedCallback(); 
      if (!this._clientClicksBound) {
        this._clientClicksBound = true;
        this.addEventListener('click', (event) => {
          if (!this.clientPager) return;
          var pageBtn = event.target.closest('[data-client-page]');
          if (pageBtn) {
            event.preventDefault();
            this.goToClientPage(parseInt(pageBtn.getAttribute('data-client-page'), 10));
            return;
          }
          var loadMoreBtn = event.target.closest('[data-load-more-btn]');
          if (loadMoreBtn) {
            event.preventDefault();
            this.revealNextClientChunk(true);
          }
        });
      }
      this.initClientPagination();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.teardownClientPagination();
    }

    applySwap(next) { 
      var live = this.querySelector('[data-collection-grid]');
      var incoming = next.querySelector('[data-collection-grid]');
      if (live && incoming) {
        ['data-client-paginate', 'data-per-page'].forEach(function (attr) {
          if (incoming.hasAttribute(attr)) live.setAttribute(attr, incoming.getAttribute(attr));
          else live.removeAttribute(attr);
        });
      }
      super.applySwap(next);
      this.initClientPagination();
    }

    teardownClientPagination() {
      if (this.clientIo) { this.clientIo.disconnect(); this.clientIo = null; }
      this.clientPager = null;
    }

    initClientPagination() {
      this.teardownClientPagination();

      var staleNav = this.querySelector('[data-collection-pagination] [data-client-pagination-nav]');
      var grid = this.querySelector('[data-collection-grid][data-client-paginate]');
      var perPage = grid ? parseInt(grid.getAttribute('data-per-page'), 10) || 0 : 0;
      var items = grid
        ? Array.prototype.filter.call(grid.children, function (el) {
            return !el.matches('.search-tpl__no-results');
          })
        : [];
      if (!grid || perPage < 1 || items.length <= perPage) {
        if (staleNav) staleNav.remove();
        return;
      }

      var footer = this.querySelector('[data-collection-load-more]');
      var mode = footer ? footer.getAttribute('data-mode') : 'default';

      this.clientPager = {
        grid: grid,
        items: items,
        perPage: perPage,
        totalPages: Math.ceil(items.length / perPage),
        page: 1,
        shown: perPage,
        mode: mode,
        footer: footer
      };

      if (mode === 'default') {
        this.showClientPage(1);
        this.renderClientPagination();
      } else {
        this.applyClientShown(false);
        if (mode === 'infinite_scroll' && 'IntersectionObserver' in window && footer) {
          var self = this;
          this.clientIo = new IntersectionObserver(
            function (entries) {
              for (var i = 0; i < entries.length; i++) {
                if (entries[i].isIntersecting) { self.revealNextClientChunk(false); break; }
              }
            },
            { rootMargin: '400px 0px' }
          );
          this.clientIo.observe(footer);
        }
      }
    }

    setItemPagedOut(el, out) {
      el.classList.toggle('is-paged-out', out);
      el.hidden = out; 
      el.style.display = out ? 'none' : '';
    }

    /* ── Numbered mode ── */
    showClientPage(page) {
      var pager = this.clientPager;
      pager.page = Math.min(Math.max(page, 1), pager.totalPages);
      var start = (pager.page - 1) * pager.perPage;
      var end = start + pager.perPage;
      var self = this;
      pager.items.forEach(function (el, index) {
        self.setItemPagedOut(el, index < start || index >= end);
      });
      var status = this.querySelector('[data-collection-page-status]');
      var host = this.querySelector('[data-collection-pagination]');
      if (status && host) {
        status.textContent = (decodeEntities(host.getAttribute('data-label-page')) || 'Page [n]').replace('[n]', pager.page);
      }
    }

    goToClientPage(page) {
      if (!this.clientPager || this.clientPager.mode !== 'default') return;
      this.showClientPage(page);
      this.renderClientPagination();
      this.scrollToResults(); 
      var visible = this.clientPager.items[(this.clientPager.page - 1) * this.clientPager.perPage];
      var target = visible && visible.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!target) { this.focusResults(); return; }
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }

    renderClientPagination() {
      var pager = this.clientPager;
      var host = this.querySelector('[data-collection-pagination]');
      if (!host) return;

      var nav = host.querySelector('[data-client-pagination-nav]');
      if (!nav) {
        nav = document.createElement('nav');
        nav.className = 'number-pagination d-flex justify-content-center gap-3 mt-lg-5 pt-lg-2 mt-4 pt-1';
        nav.setAttribute('data-client-pagination-nav', '');
        nav.setAttribute('aria-label', decodeEntities(host.getAttribute('data-label-nav')) || 'Pagination');
        host.appendChild(nav);
      }
      nav.textContent = '';

      var pageLabel = decodeEntities(host.getAttribute('data-label-page')) || 'Page [n]';
      var self = this;

      function addButton(label, page, opts) {
        opts = opts || {};
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'number-pagination-item btn ' + (opts.active ? 'btn-primary active' : 'btn-outline-primary');
        if (opts.className) btn.className += ' ' + opts.className;
        if (opts.icon) btn.innerHTML = '<span class="number-pagination-item__icon" aria-hidden="true">' + opts.icon + '</span>';
        else btn.textContent = label;
        if (opts.ariaLabel) btn.setAttribute('aria-label', opts.ariaLabel);
        if (opts.active) btn.setAttribute('aria-current', 'page');
        else btn.setAttribute('data-client-page', String(page));
        nav.appendChild(btn);
      }
      function addDots() {
        var span = document.createElement('span');
        span.className = 'number-pagination-item btn btn-outline-primary dots';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = '…';
        nav.appendChild(span);
      }

      var chevronLeft = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      var chevronRight = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      if (pager.page > 1) {
        addButton('', pager.page - 1, {
          icon: chevronLeft,
          className: 'number-pagination-prev',
          ariaLabel: decodeEntities(host.getAttribute('data-label-prev')) || 'Previous'
        });
      }

      /* Same window as Liquid paginate.parts: 1 … current±2 … last */
      var lastPrinted = 0;
      for (var p = 1; p <= pager.totalPages; p++) {
        var inWindow = p === 1 || p === pager.totalPages || Math.abs(p - pager.page) <= 2;
        if (!inWindow) continue;
        if (lastPrinted && p - lastPrinted > 1) addDots();
        addButton(String(p), p, {
          active: p === pager.page,
          ariaLabel: pageLabel.replace('[n]', p)
        });
        lastPrinted = p;
      }

      if (pager.page < pager.totalPages) {
        addButton('', pager.page + 1, {
          icon: chevronRight,
          className: 'number-pagination-next',
          ariaLabel: decodeEntities(host.getAttribute('data-label-next')) || 'Next'
        });
      }
    }

    /* ── Load more / infinite mode ── */
    applyClientShown(focusFirstNew) {
      var pager = this.clientPager;
      var firstNew = null;
      var self = this;
      pager.items.forEach(function (el, index) {
        var out = index >= pager.shown;
        if (!out && el.classList.contains('is-paged-out') && !firstNew) firstNew = el;
        self.setItemPagedOut(el, out);
      });

      var footer = pager.footer;
      if (footer) {
        var done = pager.shown >= pager.items.length;
        var count = footer.querySelector('[data-load-more-count]');
        var template = decodeEntities(footer.getAttribute('data-count-template'));
        if (count && template) {
          count.textContent = template
            .replace('[current]', Math.min(pager.shown, pager.items.length))
            .replace('[total]', pager.items.length);
        }
        var btn = footer.querySelector('[data-load-more-btn]');
        if (btn) btn.hidden = done;
        var end = footer.querySelector('[data-load-more-end]');
        if (end) end.hidden = !done;
        if (done && this.clientIo) { this.clientIo.disconnect(); this.clientIo = null; }
      }

      if (focusFirstNew && firstNew) {
        var focusable = firstNew.querySelector('a, button');
        if (focusable) focusable.focus();
      }
    }

    revealNextClientChunk(focusFirstNew) {
      var pager = this.clientPager;
      if (!pager || pager.mode === 'default') return;
      if (pager.shown >= pager.items.length) return;
      pager.shown += pager.perPage;
      this.applyClientShown(focusFirstNew);
    }
  }

  if (!customElements.get('search-filters')) {
    customElements.define('search-filters', SearchFilters);
  }
})();
